import React, { useState, useEffect } from 'react';
import { useChiTietKheUocLaiSuat, useChiTietKheUocThanhToan } from '@/features/danh-muc/hop-dong-khe-uoc/khe-uoc/hooks';
import { AccountModel, BoPhan, HopDong, KhachHang, KheUoc, KheUocInput } from '@/types/schemas';
import { BottomActionBarV2, AritoIcon, DialogV2, AritoFormV2 } from '@/components/custom/erp';
import { ChiTietThanhToanTab, GeneralInfoTab, ThongTinLaiSuatTab } from './tabs';
import { FormSchema, FormValues, initialFormValues } from '../schema';
import BasicInfoTab from './BasicInfoTab';

interface FormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: KheUocInput) => void;
  onAdd?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onCopy?: () => void;
  initialData?: KheUoc;
  formMode: 'add' | 'edit' | 'view';
}

const FormDialog: React.FC<FormDialogProps> = ({
  open,
  onClose,
  onSubmit,
  onAdd,
  onEdit,
  onDelete,
  onCopy,
  formMode,
  initialData
}) => {
  // Form state
  const [isFormDirty, setIsFormDirty] = useState<boolean>(false);

  // Related entity state
  const [hopDong, setHopDong] = useState<HopDong | null>(initialData?.ma_hd_data || null);
  const [boPhan, setBoPhan] = useState<BoPhan | null>(initialData?.ma_bp_data || null);
  const [khachHang, setKhachHang] = useState<KhachHang | null>(initialData?.ma_kh_data || null);
  const [taiKhoan, setTaiKhoan] = useState<AccountModel | null>(initialData?.tk_data || null);

  // Interest rate details state
  const {
    rows: thongTinLaiSuatRows,
    selectedRowUuid: laiSuatSelectedRowUuid,
    handleRowClick: handleLaiSuatRowClick,
    handleAddRow: handleAddLaiSuatRow,
    handleDeleteRow: handleDeleteLaiSuatRow,
    handleCopyRow: handleCopyLaiSuatRow,
    handlePasteRow: handlePasteLaiSuatRow,
    handleMoveRow: handleMoveLaiSuatRow,
    handleCellValueChange: handleLaiSuatCellValueChange
  } = useChiTietKheUocLaiSuat(initialData?.chi_tiet_lai_suat_data || []);

  // Payment details state
  const {
    rows: chiTietThanhToanRows,
    selectedRowUuid: thanhToanSelectedRowUuid,
    handleRowClick: handleThanhToanRowClick,
    handleAddRow: handleAddThanhToanRow,
    handleDeleteRow: handleDeleteThanhToanRow,
    handleCopyRow: handleCopyThanhToanRow,
    handlePasteRow: handlePasteThanhToanRow,
    handleMoveRow: handleMoveThanhToanRow,
    handleCellValueChange: handleThanhToanCellValueChange
  } = useChiTietKheUocThanhToan(initialData?.chi_tiet_thanh_toan_data || []);

  // Reset form dirty state when dialog is closed
  useEffect(() => {
    if (!open) {
      setIsFormDirty(false);
    }
  }, [open]);

  /**
   * Format date to YYYY-MM-DD
   * @param dateString - The date string to format
   * @returns Formatted date string in YYYY-MM-DD format
   */
  const formatDate = (dateString: string): string => {
    if (!dateString) return '';

    // If it's already in YYYY-MM-DD format, return it
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }

    try {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  // Handle form submission
  const handleSubmit = async (formData: FormValues & { ma_khe_uoc?: string }) => {
    try {
      if (!khachHang?.uuid) {
        throw new Error('Vui lòng chọn khách hàng');
      }
      // Prepare data for submission
      const submitData: KheUocInput = {
        ...formData,
        ma_hd: hopDong?.uuid || '',
        ma_bp: boPhan?.uuid || '',
        ma_kh: khachHang.uuid,
        tien: formData.tien_nt,
        tk: taiKhoan?.uuid || '',
        chi_tiet_lai_suat: thongTinLaiSuatRows.map((row, index) => ({
          ma_ku: formData.ma_khe_uoc,
          line: index + 1,
          ls: Number(row.ls),
          ngay_hl: formatDate(row.ngay_hl),
          ghi_chu: row.ghi_chu
        })),
        chi_tiet_thanh_toan: chiTietThanhToanRows.map((row, index) => ({
          ma_ku: formData.ma_khe_uoc,
          line: index + 1,
          ngay_tt: formatDate(row.ngay_tt),
          tt: Number(row.tt),
          tt_nt: Number(row.tt_nt),
          ghi_chu: row.ghi_chu
        }))
      };

      // Submit the data and wait for the response
      await onSubmit(submitData);

      // Reset form state
      setIsFormDirty(false);
    } catch (err: unknown) {
      console.error('Error submitting form:', err);
    }
  };

  // Handle dialog close

  // Get dialog title based on form mode
  const getDialogTitle = () => {
    switch (formMode) {
      case 'add':
        return 'Thêm khế ước';
      case 'edit':
        return 'Sửa khế ước';
      case 'view':
        return 'Xem khế ước';
      default:
        return 'Khế ước';
    }
  };

  return (
    <>
      <DialogV2
        open={open}
        onClose={onClose}
        title={getDialogTitle()}
        maxWidth='xl'
        disableBackdropClose={false}
        disableEscapeKeyDown={true}
        titleIcon={<AritoIcon icon={281} />}
      >
        <AritoFormV2
          mode={formMode}
          hasAritoActionBar={false}
          schema={FormSchema}
          onSubmit={handleSubmit}
          initialData={
            (formMode === 'add' && initialData
              ? {
                  ...(initialData as unknown as FormValues),
                  uuid: undefined,
                  ma_khe_uoc: ''
                }
              : initialData || initialFormValues) as FormValues
          }
          className='w-[800px]'
          headerFields={<BasicInfoTab formMode={formMode} />}
          tabs={[
            {
              id: 'thong-tin-chung',
              label: 'Thông tin chung',
              component: (
                <GeneralInfoTab
                  formMode={formMode}
                  hopDong={hopDong}
                  setHopDong={setHopDong}
                  boPhan={boPhan}
                  setBoPhan={setBoPhan}
                  khachHang={khachHang}
                  setKhachHang={setKhachHang}
                  taiKhoan={taiKhoan}
                  setTaiKhoan={setTaiKhoan}
                />
              )
            },
            {
              id: 'thong-tin-lai-suat',
              label: 'Thông tin lãi suất',
              component: (
                <ThongTinLaiSuatTab
                  mode={formMode}
                  rows={thongTinLaiSuatRows}
                  selectedRowUuid={laiSuatSelectedRowUuid}
                  onRowClick={handleLaiSuatRowClick}
                  onAddRow={handleAddLaiSuatRow}
                  onDeleteRow={handleDeleteLaiSuatRow}
                  onCopyRow={handleCopyLaiSuatRow}
                  onPasteRow={handlePasteLaiSuatRow}
                  onMoveRow={handleMoveLaiSuatRow}
                  onCellValueChange={handleLaiSuatCellValueChange}
                />
              )
            },
            {
              id: 'chi-tiet-thanh-toan',
              label: 'Chi tiết thanh toán',
              component: (
                <ChiTietThanhToanTab
                  mode={formMode}
                  rows={chiTietThanhToanRows}
                  selectedRowUuid={thanhToanSelectedRowUuid}
                  onRowClick={handleThanhToanRowClick}
                  onAddRow={handleAddThanhToanRow}
                  onDeleteRow={handleDeleteThanhToanRow}
                  onCopyRow={handleCopyThanhToanRow}
                  onPasteRow={handlePasteThanhToanRow}
                  onMoveRow={handleMoveThanhToanRow}
                  onCellValueChange={handleThanhToanCellValueChange}
                />
              )
            }
          ]}
          bottomBar={
            <BottomActionBarV2
              mode={formMode}
              onAdd={onAdd}
              onEdit={onEdit}
              onDelete={onDelete}
              onCopy={onCopy}
              onSubmit={() => {}}
              onClose={onClose}
            />
          }
        />
      </DialogV2>
    </>
  );
};

export default FormDialog;
